import { useEffect, useMemo, useState, type ReactNode, type ChangeEvent } from 'react';
import * as XLSX from 'xlsx';
import { deleteInsumo, getInsumos, getInsumosPaginados, cargaMasivaInsumos, exportarInsumos, getExportarInsumosStatus, descargarExportacionInsumos } from '../../lib';
import type { Insumo } from '../../lib/appTypes';
import type { CargaMasivaInsumosResult } from '../../lib/bodega';
import { SectionSurface } from '../../components/role-ui';
import FormInsumo from './FormInsumo';
import { Modal, confirmar, SkeletonRows } from '../../components/ui-kit';

function PanelPopup({
  children,
  onClose,
}: {
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} maxWidthClassName="max-w-4xl">
      <div className="px-6 pb-6 pt-14">{children}</div>
    </Modal>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-EC', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

export default function BodegaInsumosPanel() {
  const [insumos, setInsumos] = useState<Insumo[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingInsumo, setEditingInsumo] = useState<Insumo | null>(null);

  // Paginación servidor
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // Búsqueda servidor
  const [busqueda, setBusqueda] = useState('');

  // Cards superiores
  const [totalesCards, setTotalesCards] = useState({ valorRef: 0, stock: 0 });

  // Carga masiva
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [cargaResultado, setCargaResultado] = useState<CargaMasivaInsumosResult | null>(null);
  const [cargaFileContent, setCargaFileContent] = useState('');

  async function refresh(p = page) {
    try {
      setLoading(true);
      const result = await getInsumosPaginados({
        page: p,
        page_size: pageSize,
        search: busqueda.trim() || undefined,
      });
      setInsumos(result.items);
      setTotalPages(result.totalPages);
      setTotalCount(result.count);
      setPage(result.page);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudieron cargar los insumos.');
    } finally {
      setLoading(false);
    }
  }

  async function loadTotales() {
    try {
      const all = await getInsumos({ page_size: 9999, order_by: 'nombre', order_up: true });
      setTotalesCards({
        valorRef: all.reduce((t, i) => t + Number(i.precio || 0), 0),
        stock: all.reduce((t, i) => t + Number(i.stockDisponible || 0), 0),
      });
    } catch { /* silencioso */ }
  }

  useEffect(() => {
    void refresh(1);
    void loadTotales();
  }, []);

  function handleSearch() {
    setPage(1);
    void refresh(1);
  }

  async function handleLimpiarFiltros() {
    setBusqueda('');
    setPage(1);
    try {
      setLoading(true);
      const result = await getInsumosPaginados({ page: 1, page_size: pageSize });
      setInsumos(result.items);
      setTotalPages(result.totalPages);
      setTotalCount(result.count);
      setPage(1);
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudieron cargar los insumos.');
    } finally {
      setLoading(false);
    }
  }

  function goToPage(p: number) {
    setPage(p);
    void refresh(p);
  }

  async function handleRefreshAfterAction() {
    await loadTotales();
    await refresh(page);
  }

  async function handleEliminarInsumo(insumo: Insumo) {
    if (!(await confirmar({ titulo: `¿Eliminar el insumo ${insumo.nombre}?`, mensaje: 'Esta acción no se puede deshacer.', textoAceptar: 'Eliminar' }))) return;

    try {
      await deleteInsumo(insumo.id);
      await handleRefreshAfterAction();
      setDataError('');
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'No se pudo eliminar el insumo. Verifica si tiene asignaciones asociadas.');
    }
  }

  // ── Exportar XLSX via Celery ────────────────────────────────────────────────
  const [exportando, setExportando] = useState(false);

  async function handleExportXLSX() {
    if (exportando) return;
    try {
      setExportando(true);
      setDataError('');

      // 1. Iniciar tarea
      const task = await exportarInsumos();
      if (!task.task_id) {
        throw new Error('No se obtuvo ID de tarea.');
      }

      // 2. Poll hasta que esté listo
      let attempts = 0;
      const maxAttempts = 60; // ~2 min @ 2s
      let fileUrl = '';
      let fileName = 'Insumos.xlsx';

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const status = await getExportarInsumosStatus(task.task_id);
        attempts++;

        if (status.status === 'SUCCESS') {
          fileUrl = status.file_url || '';
          fileName = status.file_name || 'Insumos.xlsx';
          break;
        }
        if (status.status === 'FAILURE') {
          throw new Error(status.error || 'La exportación falló.');
        }
      }

      if (!fileUrl) {
        throw new Error('La exportación no se completó a tiempo. Intenta de nuevo.');
      }

      // 3. Descargar
      await descargarExportacionInsumos(task.task_id, fileName);
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'Error al exportar insumos.');
    } finally {
      setExportando(false);
    }
  }

  // ── Carga masiva ──────────────────────────────────────────────────────────

  function descargarPlantillaExcel() {
    const wb = XLSX.utils.book_new();
    const data = [
      { Nombre: 'Filtro de aceite', Marca: 'FRAM', Precio: 12.50, Stock: 30, 'Identificación Proveedor': '1790012345001' },
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    // Ajustar ancho de columnas
    ws['!cols'] = [
      { wch: 25 },
      { wch: 15 },
      { wch: 10 },
      { wch: 10 },
      { wch: 25 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Insumos');
    XLSX.writeFile(wb, 'Plantilla_Insumos.xlsx');
  }

  async function handleCargaMasiva(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingExcel(true);
    setDataError('');
    try {
      const content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      setCargaFileContent(content);
      const result = await cargaMasivaInsumos(content, false);
      setCargaResultado(result);

      if (!result.hay_duplicados) {
        await refresh();
      }
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'Error al procesar la carga masiva.');
    } finally {
      setUploadingExcel(false);
      event.target.value = '';
    }
  }

  async function handleSumarStockDuplicados() {
    if (!cargaFileContent) return;
    setUploadingExcel(true);
    setDataError('');
    try {
      const result = await cargaMasivaInsumos(cargaFileContent, true);
      setCargaResultado(result);
      await refresh();
    } catch (err: unknown) {
      setDataError((err as { message?: string })?.message ?? 'Error al sumar stock de duplicados.');
    } finally {
      setUploadingExcel(false);
    }
  }

  function cerrarModalCarga() {
    setCargaResultado(null);
    setCargaFileContent('');
  }

  return (
    <>
      <section className="space-y-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void handleExportXLSX()}
              disabled={exportando}
              className="ui-btn ui-btn-info"
            >
              {exportando ? 'Exportando...' : 'Exportar XLSX'}
            </button>
            <button
              type="button"
              onClick={descargarPlantillaExcel}
              className="ui-btn ui-btn-info"
            >
              Descargar plantilla Excel
            </button>
            <label className={`inline-flex cursor-pointer items-center rounded-[14px] border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 ${uploadingExcel ? 'pointer-events-none opacity-60' : ''}`}>
              {uploadingExcel ? 'Procesando...' : 'Cargar insumos Excel'}
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleCargaMasiva}
                disabled={uploadingExcel}
                className="hidden"
              />
            </label>
            <button
              type="button"
              onClick={() => {
                setEditingInsumo(null);
                setShowForm(true);
              }}
              className="ui-btn ui-btn-primary"
            >
              Nuevo insumo
            </button>
          </div>
        </div>

        {dataError && (
          <div className="rounded-[var(--radius-panel)] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 shadow-sm">
            {dataError}
          </div>
        )}

        <div className="grid gap-4 xl:grid-cols-3">
          <article className="ui-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Total insumos</p>
            <p className="mt-4 text-3xl font-semibold text-stone-950">{totalCount}</p>
          </article>
          <article className="ui-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Valor referencial</p>
            <p className="mt-4 text-3xl font-semibold text-stone-950">{formatCurrency(totalesCards.valorRef)}</p>
          </article>
          <article className="ui-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Stock disponible</p>
            <p className="mt-4 text-3xl font-semibold text-stone-950">{totalesCards.stock}</p>
          </article>
        </div>

        <SectionSurface className="space-y-5">
          <div className="flex flex-col gap-3">
            <div>
              <h4 className="text-lg font-semibold text-stone-900">Listado de insumos</h4>
              <p className="text-sm text-stone-500">Busca por nombre, proveedor o identificación del proveedor.</p>
            </div>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                  Buscar insumo
                </label>
                <input
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder="Aceite, filtro, proveedor..."
                  className="ui-input"
                />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={handleSearch} className="ui-btn ui-btn-dark">Buscar</button>
                <button type="button" onClick={() => void handleLimpiarFiltros()} className="ui-btn ui-btn-secondary">Limpiar</button>
              </div>
            </div>
          </div>

          {loading ? (
            <SkeletonRows etiqueta="Cargando insumos..." />
          ) : insumos.length === 0 ? (
            <div className="rounded-[var(--radius-card)] border border-dashed border-stone-300 bg-white/70 p-10 text-center text-stone-500">
              No hay insumos que coincidan con la búsqueda.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="ui-tabla min-w-full border-separate border-spacing-y-2 text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-[0.18em] text-[var(--freisa-text-muted)]">
                      <th className="pb-2 pr-4 font-semibold">Insumo</th>
                      <th className="pb-2 pr-4 font-semibold">Marca</th>
                      <th className="pb-2 pr-4 font-semibold">Modelo</th>
                      <th className="pb-2 pr-4 font-semibold">Proveedor</th>
                      <th className="pb-2 pr-4 font-semibold">Identificación proveedor</th>
                      <th className="pb-2 pr-4 font-semibold">Precio</th>
                      <th className="pb-2 pr-4 font-semibold">Stock disponible</th>
                      <th className="pb-2 font-semibold text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {insumos.map(insumo => (
                      <tr key={insumo.id} className="bg-[var(--freisa-bg)]/70 text-[var(--freisa-text)]">
                        <td className="rounded-l-[16px] px-4 py-3">
                          <p className="font-semibold">{insumo.nombre}</p>
                          <p className="mt-1 text-xs text-[var(--freisa-text-soft)]">{insumo.codigo || 'Sin código visible'}</p>
                        </td>
                        <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                          {insumo.marca || '—'}
                        </td>
                        <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                          {insumo.modeloInsumo || '—'}
                        </td>
                        <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                          {insumo.proveedorNombre || '—'}
                        </td>
                        <td className="px-4 py-3 text-[var(--freisa-text-soft)]">
                          {insumo.proveedorIdentificacion || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                            {formatCurrency(insumo.precio)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                            {insumo.stockDisponible}
                          </span>
                        </td>
                        <td className="rounded-r-[16px] px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingInsumo(insumo);
                                setShowForm(false);
                              }}
                              className="ui-btn ui-btn-secondary ui-btn-sm"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleEliminarInsumo(insumo)}
                              className="ui-btn ui-btn-danger ui-btn-sm"
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-4 py-3 rounded-b-[20px]">
                <span className="text-sm text-stone-500">
                  Página {page} de {totalPages} · {totalCount} registro(s)
                </span>
                <div className="flex gap-2">
                  <button onClick={() => goToPage(page - 1)} disabled={page <= 1 || loading} className="rounded-xl border border-stone-200 bg-white px-3 py-1.5 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50">Anterior</button>
                  <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages || loading} className="ui-btn ui-btn-secondary ui-btn-sm">Siguiente</button>
                </div>
              </div>
            </>
          )}
        </SectionSurface>
      </section>

      {(showForm || editingInsumo) && (
        <PanelPopup
          onClose={() => {
            setShowForm(false);
            setEditingInsumo(null);
          }}
        >
          <FormInsumo
            initialInsumo={editingInsumo}
            onSaved={() => {
              setShowForm(false);
              setEditingInsumo(null);
              void handleRefreshAfterAction();
            }}
            onCancel={() => {
              setShowForm(false);
              setEditingInsumo(null);
            }}
          />
        </PanelPopup>
      )}

      {cargaResultado && (
        <PanelPopup onClose={cerrarModalCarga}>
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-stone-900">Resultado de carga masiva</h3>
              <p className="text-sm text-stone-500">
                {cargaResultado.total_procesados} filas procesadas.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[var(--radius-card)] border border-emerald-200 bg-emerald-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Creados</p>
                <p className="mt-2 text-2xl font-bold text-emerald-900">{cargaResultado.creados.length}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-blue-200 bg-blue-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Actualizados</p>
                <p className="mt-2 text-2xl font-bold text-blue-900">{cargaResultado.actualizados.length}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-amber-200 bg-amber-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Duplicados</p>
                <p className="mt-2 text-2xl font-bold text-amber-900">{cargaResultado.duplicados.length}</p>
              </div>
              <div className="rounded-[var(--radius-card)] border border-red-200 bg-red-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-700">Errores</p>
                <p className="mt-2 text-2xl font-bold text-red-900">{cargaResultado.errores.length}</p>
              </div>
            </div>

            {cargaResultado.creados.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-emerald-800 mb-2">Insumos creados</p>
                <div className="max-h-40 overflow-y-auto rounded-2xl border border-emerald-100 bg-white text-sm">
                  {cargaResultado.creados.map((item, i) => (
                    <p key={i} className="border-b border-emerald-50 px-4 py-2 text-stone-700 last:border-0">
                      {item.nombre} — ${item.precio.toFixed(2)} — Stock: {item.stock}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {cargaResultado.actualizados.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-blue-800 mb-2">Insumos con stock actualizado</p>
                <div className="max-h-40 overflow-y-auto rounded-2xl border border-blue-100 bg-white text-sm">
                  {cargaResultado.actualizados.map((item, i) => (
                    <p key={i} className="border-b border-blue-50 px-4 py-2 text-stone-700 last:border-0">
                      {item.nombre} — Stock: {item.stock_anterior} → {item.stock_nuevo}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {cargaResultado.duplicados.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-2">
                  Productos ya registrados (no se sumó el stock)
                </p>
                <div className="max-h-40 overflow-y-auto rounded-2xl border border-amber-100 bg-white text-sm">
                  {cargaResultado.duplicados.map((item, i) => (
                    <p key={i} className="border-b border-amber-50 px-4 py-2 text-stone-700 last:border-0">
                      {item.nombre} — Stock actual: {item.stock_actual}
                    </p>
                  ))}
                </div>
                <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  ¿Deseas sumar el stock del Excel al stock actual de estos productos ya registrados?
                </div>
                <button
                  type="button"
                  onClick={() => void handleSumarStockDuplicados()}
                  disabled={uploadingExcel}
                  className="ui-btn ui-btn-warning mt-3"
                >
                  {uploadingExcel ? 'Procesando...' : 'Sí, sumar stock a existentes'}
                </button>
              </div>
            )}

            {cargaResultado.errores.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-red-800 mb-2">Errores encontrados</p>
                <div className="max-h-40 overflow-y-auto rounded-2xl border border-red-100 bg-white text-sm">
                  {cargaResultado.errores.map((err, i) => (
                    <p key={i} className="border-b border-red-50 px-4 py-2 text-red-700 last:border-0">
                      {err}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={cerrarModalCarga}
                className="ui-btn ui-btn-primary"
              >
                Cerrar
              </button>
            </div>
          </div>
        </PanelPopup>
      )}
    </>
  );
}
